<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { FileText } from 'lucide-vue-next'

import StatusBadge from '@/components/applications/StatusBadge.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { formatAmount, formatLabel } from '@/lib/utils'
import { portalCustomerApplicationPath, portalCustomerApplicationsPath } from '@/routes'

interface Customer {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string
  date_of_birth: string
  gender: string
}

function formatDob(dob: string) {
  return new Date(dob).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

interface ActiveApplication {
  id: string
  status: string
  loan_type: string
  purpose: string
  amount: number
  created_at: string
  taken_at: string | null
  accepted_at: string | null
  approved_at: string | null
}

defineProps<{ customer: Customer; active_application: ActiveApplication | null }>()

</script>

<template>
  <Head title="Dashboard" />
  <PortalLayout actor-type="customer">
    <div class="space-y-6">
      <h1 class="text-xl font-semibold">Welcome, {{ customer.first_name }}!</h1>

      <!-- Active application card -->
      <Card v-if="active_application">
        <CardHeader>
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">Current Application</CardTitle>
              <CardDescription>{{ formatLabel(active_application.purpose) }} · {{ formatAmount(active_application.amount) }}</CardDescription>
            </div>
            <StatusBadge :status="active_application.status" />
          </div>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" as-child>
            <Link :href="portalCustomerApplicationPath(active_application.id)">View details →</Link>
          </Button>
        </CardContent>
      </Card>

      <!-- No active application -->
      <Card v-else>
        <CardContent class="flex flex-col items-center gap-3 py-12 text-center">
          <FileText class="text-muted-foreground/40 size-10" />
          <p class="text-muted-foreground text-sm">No active application</p>
          <Button variant="outline" size="sm" as-child>
            <Link :href="portalCustomerApplicationsPath()">View all applications</Link>
          </Button>
        </CardContent>
      </Card>

      <!-- My Details -->
      <Card>
        <CardHeader>
          <CardTitle class="text-base">My Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl class="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <dt class="text-muted-foreground">Full Name</dt>
              <dd class="mt-1 font-medium">{{ customer.first_name }} {{ customer.last_name }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Email</dt>
              <dd class="mt-1 font-medium">{{ customer.email }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Phone</dt>
              <dd class="mt-1 font-medium">{{ customer.phone }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Date of Birth</dt>
              <dd class="mt-1 font-medium">{{ formatDob(customer.date_of_birth) }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Gender</dt>
              <dd class="mt-1 font-medium">{{ customer.gender }}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  </PortalLayout>
</template>
